class ShippingInsurance extends HTMLElement {
  constructor() {
    super();
    
    // Bind methods to ensure 'this' context is correct when used as event handlers
    this.handleToggleChange = this.handleToggleChange.bind(this);
    this.handleImageError = this.handleImageError.bind(this);
    this.handleImageLoad = this.handleImageLoad.bind(this);
    this.handleTargetingChange = this.handleTargetingChange.bind(this);
  }

  connectedCallback() {
    this.toggle = this.querySelector('.shipping-insurance__toggle');
    this.container = this.querySelector('.shipping-insurance__container');
    this.loadingElement = this.querySelector('.shipping-insurance__loading');
    this.errorElement = this.querySelector('.shipping-insurance__error');
    this.errorText = this.querySelector('.shipping-insurance__error-text');
    this.productImage = this.querySelector('.shipping-insurance__image');
    
    if (this.toggle) {
      this.toggle.addEventListener('change', this.handleToggleChange);
    }
    
    // Handle image loading errors
    if (this.productImage) {
      this.productImage.addEventListener('error', this.handleImageError);
      this.productImage.addEventListener('load', this.handleImageLoad);
    }
    
    const updateInsuranceState = () => {
      const toggle = this.querySelector('.shipping-insurance__toggle');
      if(typeof CartItemList !== 'undefined' && CartItemList.length === 0 && toggle && toggle.checked === false){
        let variantId = toggle.dataset.variantId;
        toggle.checked = true;
        this.addInsuranceToCart(variantId);
      }
    }
    updateInsuranceState();

    document.addEventListener('targeting-changed', this.handleTargetingChange);
  }
  
  disconnectedCallback() {
    if (this.toggle) {
      this.toggle.removeEventListener('change', this.handleToggleChange);
    }
    if (this.productImage) {
      this.productImage.removeEventListener('error', this.handleImageError);
      this.productImage.removeEventListener('load', this.handleImageLoad);
    }
    document.removeEventListener('targeting-changed', this.handleTargetingChange);
    
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  handleTargetingChange(e) {
    if (e.key === "CART_CHANGE" && typeof CartItemList !== 'undefined') {
      CartItemList = e.value.items;
    }
  }
  
  handleToggleChange(event) {
    event.preventDefault();
    
    const isChecked = event.target.checked;
    const variantId = event.target.dataset.variantId;
    
    console.log("isChecked",isChecked);
    if (isChecked) {
      this.addInsuranceToCart(variantId);
    } else {
      this.removeInsuranceFromCart(variantId);
    }
  }
  
  handleImageError(event) {
    console.warn('Shipping insurance product image failed to load');
    // Hide the image and show fallback icon
    const image = event.target;
    const icon = this.querySelector('.shipping-insurance__icon');
    const svgWrapper = icon.querySelector('.svg-wrapper');
    
    if (image && svgWrapper) {
      image.style.display = 'none';
      svgWrapper.style.display = 'flex';
      svgWrapper.style.alignItems = 'center';
      svgWrapper.style.justifyContent = 'center';
    }
  }
  
  handleImageLoad(event) {
    // Image loaded successfully, hide the fallback icon
    const icon = this.querySelector('.shipping-insurance__icon');
    const svgWrapper = icon.querySelector('.svg-wrapper');
    
    if (svgWrapper) {
      svgWrapper.style.display = 'none';
    }
  }
  
  async addInsuranceToCart(variantId) {
    this.showLoading();
    this.hideError();
    
    try {
      // Validate variant ID
      if (!variantId || variantId === 'null' || variantId === 'undefined') {
        throw new Error('Invalid product variant. Please refresh the page and try again.');
      }
      
      // Get current cart to build update payload
      const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
      if (!cartResponse.ok) {
        throw new Error('Failed to fetch current cart information');
      }
      
      const cartData = await cartResponse.json();
      
      // Build updates object with current cart items
      const updates = {};
      
      // Preserve existing items (except insurance)
      cartData.items.forEach(item => {
        // Skip existing insurance items to avoid duplicates
        if (item.handle !== this.dataset.productHandle && 
            !(item.properties && item.properties['_shipping_insurance'] === 'true')) {
          updates[item.variant_id] = item.quantity;
        }
      });
      console.log("updates",cartData);
      // Add insurance with quantity 1
      updates[parseInt(variantId)] = 1;
      
      const formData = {
        updates: updates,
        attributes: {
          '_shipping_insurance_added': 'true'
        }
      };
      
      const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        let errorMessage = 'Failed to add shipping insurance';
        
        if (errorData.description) {
          errorMessage = errorData.description;
        } else if (response.status === 422) {
          errorMessage = 'This product is currently unavailable';
        } else if (response.status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        }
        
        throw new Error(errorMessage);
      }
      
      const updatedCartData = await response.json();
      
      // Update cart drawer
      await this.updateCartDrawer();
      
      // Publish cart update event
      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { 
          source: 'shipping-insurance', 
          cartData: updatedCartData,
          action: 'add'
        });
      }
      
    } catch (error) {
      console.error('Error adding shipping insurance:', error);
      this.showError(error.message);
      // Uncheck the toggle on error
      if (this.toggle) {
        this.toggle.checked = false;
      }
    } finally {
      this.hideLoading();
    }
  }
  
  async removeInsuranceFromCart(variantId) {
    this.showLoading();
    this.hideError();
    
    try {
      if (!variantId) {
        variantId = this.dataset.variantId;
      }

      if (!variantId || variantId === 'null' || variantId === 'undefined') {
        throw new Error('Invalid product variant for removal.');
      }

      const formData = {
        updates: {
          [variantId]: 0
        },
        attributes: {
          '_shipping_insurance_added': 'false'
        }
      };
      
      const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        let errorMessage = 'Failed to remove shipping insurance';
        
        if (errorData.description) {
          errorMessage = errorData.description;
        } else if (response.status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        }
        
        throw new Error(errorMessage);
      }
      
      const updatedCartData = await response.json();
      
      // Update cart drawer
      await this.updateCartDrawer();
      
      // Publish cart update event
      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { 
          source: 'shipping-insurance', 
          cartData: updatedCartData,
          action: 'remove'
        });
      }
      
    } catch (error) {
      console.error('Error removing shipping insurance:', error);
      this.showError(error.message);
      // Check the toggle back on error
      if (this.toggle) {
        this.toggle.checked = true;
      }
    } finally {
      this.hideLoading();
    }
  }
  
  async updateCartDrawer(sections = null) {
    try {
      if (sections) {
        // If sections are returned from cart/change.js, use them
        const cartDrawerSection = sections['cart-drawer'];
        if (cartDrawerSection) {
          const cartDrawerHtml = new DOMParser().parseFromString(cartDrawerSection, 'text/html');

          const newCartDrawerItems = cartDrawerHtml.querySelector('cart-drawer-items');
          const currentCartDrawerItems = document.querySelector('cart-drawer-items');
          if (newCartDrawerItems && currentCartDrawerItems) {
            currentCartDrawerItems.replaceWith(newCartDrawerItems);
          }

          const newCartFooter = cartDrawerHtml.querySelector('.cart-drawer__footer');
          const currentCartFooter = document.querySelector('.cart-drawer__footer');
          if (newCartFooter && currentCartFooter) {
            currentCartFooter.replaceWith(newCartFooter);
          }
        }
        
        const cartIconBubbleSection = sections['cart-icon-bubble'];
        if (cartIconBubbleSection) {
          const newCartIconBubble = new DOMParser().parseFromString(cartIconBubbleSection, 'text/html').querySelector('.shopify-section');
          const currentCartIconBubble = document.getElementById('cart-icon-bubble');
          if (newCartIconBubble && currentCartIconBubble) {
            currentCartIconBubble.innerHTML = newCartIconBubble.innerHTML;
          }
        }
        return;
      }

      const response = await fetch(`${routes.cart_url}?section_id=cart-drawer`);
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      
      
      // Update cart drawer items
      const newCartDrawerItems = html.querySelector('cart-drawer-items');
      const currentCartDrawerItems = document.querySelector('cart-drawer-items');

      // console.log("newCartDrawerItems",newCartDrawerItems);
     
      
      if (newCartDrawerItems && currentCartDrawerItems) {
        currentCartDrawerItems.replaceWith(newCartDrawerItems);
      }
      
      // Update cart drawer footer
      const newCartFooter = html.querySelector('.cart-drawer__footer');
      const currentCartFooter = document.querySelector('.cart-drawer__footer');
      
      if (newCartFooter && currentCartFooter) {
        currentCartFooter.replaceWith(newCartFooter);
      }
      
      // Update cart icon bubble
      const cartIconBubbleResponse = await fetch(`${window.location.pathname}?section_id=cart-icon-bubble`);
      const cartIconBubbleText = await cartIconBubbleResponse.text();
      const cartIconBubbleHtml = new DOMParser().parseFromString(cartIconBubbleText, 'text/html');
      const newCartIconBubble = cartIconBubbleHtml.querySelector('.shopify-section');
      const currentCartIconBubble = document.getElementById('cart-icon-bubble');
      
      if (newCartIconBubble && currentCartIconBubble) {
        currentCartIconBubble.innerHTML = newCartIconBubble.innerHTML;
      }
      
    } catch (error) {
      console.error('Error updating cart drawer:', error);
    }
  }
  
  async updateInsuranceState() {
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart.js');
      if (!response.ok) {
        console.error('Failed to fetch cart data for insurance state update');
        return;
      }
      
      const cartData = await response.json();
      
      const insuranceHandle = this.dataset.productHandle;
      let insuranceInCart = false;
      let insuranceItem = null;
      

      
      // CartItemList = cartData.items; 
      // Check for insurance by handle or properties
      cartData.items.forEach(item => {
        if (item.handle === insuranceHandle || 
            (item.properties && item.properties['_shipping_insurance'] === 'true')) {
          insuranceInCart = true;
          insuranceItem = item;
        }
      });
      
      // Update toggle state
      if (this.toggle) {
        this.toggle.checked = insuranceInCart;
      }
      
      // Refresh component if state changed externally
      if (this.toggle && this.toggle.checked !== insuranceInCart) {
        await this.refreshComponent();
      }
      
    } catch (error) {
      console.error('Error updating insurance state:', error);
    }
  }
  
  async refreshComponent() {
    try {
      const response = await fetch(`${window.location.pathname}?section_id=cart-drawer`);
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const newInsuranceComponent = html.querySelector('.shipping-insurance');
      
      if (newInsuranceComponent) {
        // Store current parent for replacement
        const parent = this.parentNode;
        const newElement = document.createElement('shipping-insurance');
        newElement.innerHTML = newInsuranceComponent.innerHTML;
        
        // Copy data attributes
        Array.from(newInsuranceComponent.attributes).forEach(attr => {
          newElement.setAttribute(attr.name, attr.value);
        });
        
        parent.replaceChild(newElement, this);
      }
    } catch (error) {
      console.error('Error refreshing insurance component:', error);
    }
  }
  
  showLoading() {
    this.classList.add('is-loading');
    if (this.loadingElement) {
      this.loadingElement.removeAttribute('hidden');
    }
  }
  
  hideLoading() {
    this.classList.remove('is-loading');
    if (this.loadingElement) {
      this.loadingElement.setAttribute('hidden', '');
    }
  }
  
  showError(message) {
    if (this.errorElement && this.errorText) {
      this.errorText.textContent = message;
      this.errorElement.removeAttribute('hidden');
    }
  }
  
  hideError() {
    if (this.errorElement) {
      this.errorElement.setAttribute('hidden', '');
    }
  }
}

// Define the custom element
customElements.define('shipping-insurance', ShippingInsurance);
// Helper function to setup image error handling
function setupImageErrorHandling(component) {
  const image = component.querySelector('.shipping-insurance__image');
  const svgWrapper = component.querySelector('.svg-wrapper');
  
  if (image && svgWrapper) {
    // Initially hide the SVG if image is present
    svgWrapper.style.display = 'none';
    
    image.addEventListener('error', function() {
      console.warn('Shipping insurance product image failed to load');
      image.style.display = 'none';
      svgWrapper.style.display = 'flex';
      svgWrapper.style.alignItems = 'center';
      svgWrapper.style.justifyContent = 'center';
    });
    
    image.addEventListener('load', function() {
      svgWrapper.style.display = 'none';
    });
    
    // Check if image is already loaded (cached)
    if (image.complete && image.naturalHeight !== 0) {
      svgWrapper.style.display = 'none';
    }
  }
}

// Handle cases where the cart drawer is dynamically loaded
document.addEventListener('DOMContentLoaded', function() {
  // Initialize any shipping insurance components that might be present
  const insuranceComponents = document.querySelectorAll('.shipping-insurance:not([is="shipping-insurance"])');
  insuranceComponents.forEach(component => {
    if (!component.matches('shipping-insurance')) {
      // Setup image error handling for non-custom elements
      setupImageErrorHandling(component);
      
      // Wrap in custom element if not already
      const wrapper = document.createElement('shipping-insurance');
      component.parentNode.insertBefore(wrapper, component);
      wrapper.appendChild(component);
    }
  });
});

// Re-initialize components after cart updates
if (typeof subscribe !== 'undefined') {
  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    // Re-initialize shipping insurance components after cart updates
    setTimeout(() => {
      const insuranceComponents = document.querySelectorAll('.shipping-insurance:not([is="shipping-insurance"])');
      insuranceComponents.forEach(component => {
        if (!component.closest('shipping-insurance')) {
          // Setup image error handling for newly loaded components
          setupImageErrorHandling(component);
          
          const wrapper = document.createElement('shipping-insurance');
          wrapper.innerHTML = component.outerHTML;
          component.replaceWith(wrapper);
        }
      });
    }, 100);
  });
}
